<template>
  <div class="comments-panel h-full flex flex-col">
    <v-sheet
      class="no-shrink py-3 d-flex align-center justify-space-between"
      elevation="0"
    >
      <v-list-item-title class="font-weight-bold"
        >{{ $t('unitStudy.comments.title') }} ({{ safeCount }})</v-list-item-title
      >
      <v-btn
        icon="mdi-close"
        variant="text"
        @click="$store.dispatch('AppData/commentsOpen', false)"
      />
    </v-sheet>

    <v-divider />

    <div v-if="loading" class="pa-4 text-medium-emphasis">{{ $t('unitStudy.comments.loading') }}</div>
    <div v-else-if="safeItems.length === 0" class="pa-4 text-medium-emphasis">
      {{ $t('unitStudy.comments.emptyState') }}
    </div>

    <v-list v-else density="compact" class="flex-1 overflow-y-auto">
      <template v-for="(group, section) in groupedBySection" :key="section">
        <v-list-subheader
          class="font-weight-bold text-grey-darken-3 text-subtitle-2 no-left-pad"
          >{{ sectionLabel(section) }}</v-list-subheader
        >

        <v-list-item
          v-for="c in group"
          :key="c.id"
          @click="goTo(c.commentParentID)"
          class="cursor-pointer py-2 comment-item with-border"
          :class="{ 'pl-0': $vuetify.display.smAndDown }"
        >
          <v-list-item-title class="comment-title two-line-ellipsis">
            {{ (c.comment || '').trim() }}
          </v-list-item-title>

          <template #append>
            <v-btn
              size="x-small"
              icon
              variant="text"
              @click.stop="openEdit(c)"
              :aria-label="$t('unitStudy.comments.editComment')"
            >
              <v-icon size="18" color="#a5d867">mdi-pencil</v-icon>
            </v-btn>

            <v-btn
              size="x-small"
              icon
              variant="text"
              @click.stop="remove(c.id)"
              :aria-label="$t('unitStudy.comments.deleteAriaLabel')"
            >
              <v-icon size="18">mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-list-item>
      </template>
    </v-list>

    <!-- Dialog (uses your existing CommentDialog.vue API) -->
    <CommentDialog
      v-model="dialogOpen"
      :paragraph-id="editing?.commentParentID || ''"
      :comment="editing?.comment || ''"
      :activecourse="activecourse"
      :title="$t('unitStudy.comments.editComment')"
      :saving="saving"
      @save="handleSave"
      @cancel="handleCancel"
    />
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import db from '@/firebase/init'
  import CommentDialog from '@/components/comments/CommentDialog.vue'

  const SECTION_ORDER = [
    'other', // "In The Bible"
    'truth',
    'equip',
    'application', // Accountability
    'mission',
    'supplication',
  ]

  export default {
    name: 'CommentsPanel',
    components: { CommentDialog },
    props: {
      courseId: { type: String, required: true },
      unitId: { type: String, required: true },
      sectionId: { type: String, required: true },
      // Forwarded down to CommentDialog so your year styles apply
      activecourse: { type: Array, default: () => [] },
    },
    data() {
      return {
        dialogOpen: false,
        editing: null, // { id, commentParentID, comment, ... }
        saving: false,
      }
    },
    computed: {
      ...mapGetters('Comments', {
        items: 'all',
        loading: 'loading',
        error: 'error',
        rawCount: 'count',
      }),
      safeItems() {
        return Array.isArray(this.items) ? this.items : []
      },
      safeCount() {
        return typeof this.rawCount === 'number'
          ? this.rawCount
          : this.safeItems.length
      },
      groupedBySection() {
        const buckets = {}

        for (const c of this.safeItems) {
          const section = this.resolveSectionFromId(c.commentParentID)
          if (!buckets[section]) buckets[section] = []
          buckets[section].push(c)
        }

        // return an ordered object so v-for respects SECTION_ORDER
        const ordered = {}
        for (const key of SECTION_ORDER) {
          if (buckets[key]?.length) ordered[key] = buckets[key]
        }
        return ordered
      },
    },
    methods: {
      trim(t) {
        const s = (t || '').trim()
        return s.length > 140 ? s.slice(0, 140) + '…' : s
      },
      goTo(paragraphId) {
        const el = document.getElementById(paragraphId)
        if (!el) return

        el.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Find the inner comment box if it exists
        const commentBox = el.querySelector('.comment-container') || el

        commentBox.classList.add('comment-flash')
        setTimeout(() => commentBox.classList.remove('comment-flash'), 1200)
      },
      async remove(docId) {
        // grab paragraph id before removal
        const item = this.safeItems.find((x) => x.id === docId)
        await this.$store.dispatch('Comments/remove', docId)
        if (item) this.patchInline(item.commentParentID, '')
      },
      start() {
        if (!this.courseId || !this.unitId || !this.sectionId) return
        this.$store.dispatch('Comments/startListening', {
          courseID: this.courseId,
          unitID: this.unitId,
          sectionID: this.sectionId,
        })
      },

      // --- Edit flow (no DOM rewiring) ---
      openEdit(c) {
        this.editing = c
        this.dialogOpen = true
      },
      handleCancel() {
        this.dialogOpen = false
        this.editing = null
        // Do nothing else—no DOM changes—so inline comment stays put.
      },
      patchInline(paragraphId, text) {
        const p = document.getElementById(paragraphId)
        if (!p) return

        let box = p.querySelector('.comment-container')
        if (text && !box) {
          box = document.createElement('div')
          box.className = 'comment-container'
          p.insertBefore(box, p.firstChild)
        }

        let icon = p.querySelector('.comment-icon-container')
        if (!icon) {
          icon = document.createElement('div')
          icon.className = 'comment-icon-container'
          p.insertBefore(icon, p.firstChild)
          icon.addEventListener('click', () => this.openCommentModule(p))
        }

        if (text) {
          box.textContent = text
          p.classList.add('active')
        } else {
          if (box) box.remove()
          p.classList.remove('active')
        }
      },
      // Map section code to user-facing name
      sectionLabel(code) {
        const map = {
          other: this.$t('unitStudy.comments.sections.inTheBible'),
          truth: this.$t('unitStudy.comments.sections.truth'),
          equip: this.$t('unitStudy.comments.sections.equipping'),
          application: this.$t('unitStudy.comments.sections.accountability'),
          mission: this.$t('unitStudy.comments.sections.mission'),
          supplication: this.$t('unitStudy.comments.sections.supplication'),
        }
        return map[code] || code
      },

      // Parse section from commentParentID (e.g., m6...-tbc-..., -mbc-..., etc.)
      resolveSectionFromId(paragraphId) {
        const pid = String(paragraphId || '')
        const m = pid.match(/-([a-z])bc-/i)
        if (!m) return 'other'
        switch (m[1].toLowerCase()) {
          case 't':
            return 'truth'
          case 'e':
            return 'equip'
          case 'a':
            return 'application' // accountability
          case 'm':
            return 'mission'
          case 's':
            return 'supplication'
          case 'i':
            return 'other' // In The Bible bucket
          default:
            return 'other'
        }
      },

      // (tiny bug fix) Use the correct module name casing when removing in edit flow
      async handleSave({ comment }) {
        if (!this.editing || !this.editing.id) return

        const trimmed = (comment || '').trim()
        this.saving = true
        try {
          const col = db.collection('coursecontent')
          if (!trimmed) {
            // was: this.$store.dispatch('comments/remove', this.editing.id)
            await this.$store.dispatch('Comments/remove', this.editing.id)
            this.patchInline(this.editing.commentParentID, '')
          } else {
            await col.doc(this.editing.id).update({ comment: trimmed })
            this.patchInline(this.editing.commentParentID, trimmed)
          }
          this.dialogOpen = false
          this.editing = null
        } finally {
          this.saving = false
        }
      },
    },
    mounted() {
      this.start()
    },
    beforeUnmount() {
      this.$store.dispatch('Comments/stopListening')
    },
    watch: {
      courseId: 'start',
      unitId: 'start',
      sectionId: 'start',
    },
  }
</script>

<style scoped>
  /* allow text area to shrink next to action buttons */
  .comment-item {
    min-width: 0;
  }

  :deep(.no-left-pad) {
    padding-inline-start: 0 !important;
  }

  /* two-line clamp with ellipsis */
  .two-line-ellipsis {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    overflow: hidden;
    white-space: normal;
    line-height: 1.35;
  }

  /* optional readability tweak */
  .comment-title {
    font-size: 0.98rem;
  }
  .with-border {
    border-bottom: 1px solid rgb(0 0 0 / 4%);
  }

  /* remove border from last item so list doesn’t look “cut off” */
  .with-border:last-of-type {
    border-bottom: none;
  }
</style>
